import React from "react";
import { CP, checkpointsFor, progressLabel } from "./tradeJourneyLogic";

/**
 * TradeMiniProgress — the 5-dot rail on an inbox card.
 *
 * Deliberately tiny (~14px) and non-interactive: it answers "how far along is
 * this one?" at a glance while scanning six trades, and the journey page is
 * where the detail lives.
 *
 * Exposed as a single `role="img"` with a written label. Five bare dots are
 * meaningless to a screen reader, and labelling each one would read out five
 * fragments instead of one sentence.
 */
const DOT = {
  [CP.DONE]: { bg: "#6d28d9", ring: "none", w: 6 },
  [CP.CURRENT]: { bg: "#6d28d9", ring: "0 0 0 3px rgba(109,40,217,0.22)", w: 7 },
  [CP.WAITING]: { bg: "#c4b5fd", ring: "none", w: 6 },
  [CP.LOCKED]: { bg: "#e2e8f0", ring: "none", w: 6 },
  [CP.MUTED]: { bg: "#e2e8f0", ring: "none", w: 5 },
  [CP.UPCOMING]: { bg: "#e2e8f0", ring: "none", w: 5 },
};

const TradeMiniProgress = ({ trade, myId }) => {
  // The presence checkpoint reads the server's `presenceRequired` off the trade
  // itself. This used to take a `presenceEnforcedFrom` prop feeding the client's
  // own copy of the gate rule, and nothing ever passed it (P2-42).
  const checkpoints = checkpointsFor(trade, myId);
  const label = progressLabel(trade, myId);

  return (
    <div
      data-testid="trade-mini-progress"
      role="img"
      aria-label={label}
      style={{ display: "flex", alignItems: "center", gap: 5, height: 14 }}
    >
      {checkpoints.map((cp, i) => {
        const style = DOT[cp.state] || DOT[CP.UPCOMING];
        return (
          <React.Fragment key={cp.key}>
            {i > 0 && (
              <span
                aria-hidden="true"
                style={{
                  flex: 1,
                  height: 2,
                  minWidth: 10,
                  borderRadius: 2,
                  background: cp.state === CP.DONE ? "#6d28d9" : "#e2e8f0",
                }}
              />
            )}
            <span
              aria-hidden="true"
              data-state={cp.state}
              style={{
                width: style.w,
                height: style.w,
                borderRadius: "50%",
                background: style.bg,
                boxShadow: style.ring === "none" ? undefined : style.ring,
                flexShrink: 0,
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default TradeMiniProgress;
